/**
 * Simple event emitter for SDK internal communication
 */

import type { EventMap, EventCallback } from '../types/events';

export class SDKEventEmitter {
  private events: Map<keyof EventMap, Set<EventCallback<any>>> = new Map();

  on<T extends keyof EventMap>(event: T, callback: EventCallback<T>): void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)!.add(callback);
  }

  off<T extends keyof EventMap>(event: T, callback: EventCallback<T>): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.events.delete(event);
      }
    }
  }

  emit<T extends keyof EventMap>(event: T, ...args: EventMap[T]): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in event callback for "${String(event)}":`, error);
        }
      });
    }
  }

  once<T extends keyof EventMap>(event: T, callback: EventCallback<T>): void {
    const onceCallback = (...args: EventMap[T]) => {
      callback(...args);
      this.off(event, onceCallback);
    };
    this.on(event, onceCallback);
  }

  removeAllListeners(): void {
    this.events.clear();
  }

  listenerCount(event: keyof EventMap): number {
    return this.events.get(event)?.size || 0;
  }
}